import { useCallback, useRef, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { useMsal } from "@azure/msal-react";
import {
  CAlert,
  CButton,
  CFormCheck,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
  CSpinner,
} from "@coreui/react-pro";
import { ScryerSpark } from "@scryerlabs/ui";
import type { ApiError } from "../../api/client";
import { legalAcceptanceDocuments } from "../../pages/legal/legal-content";
import { LegalDocumentBody } from "../../pages/legal/LegalDocumentBody";
import { useAcceptLegalDocuments } from "../../api/hooks/useAcceptLegalDocuments";
import {
  CommunicationCategory,
  useCommunicationPreferences,
  useUpdateCommunicationPreferences,
} from "../../api/hooks/useCommunicationPreferences";

/**
 * Whether a failed acceptance is worth trying again.
 *
 * A 4xx means the API refused what this build sends, and it will refuse it identically every time —
 * so "please try again" would be a lie, and this dialog blocks the whole portal, leaving the customer
 * looping on a button that cannot work. Anything else (a network drop, a 500) is worth retrying.
 */
function isRetryable(error: unknown): boolean {
  const status = (error as ApiError | null)?.status;
  return status === undefined || status < 400 || status >= 500;
}

/**
 * The acceptance gate. Blocks the portal on first sign-in, and again whenever the
 * substance of the Terms has changed since the version this customer accepted.
 *
 * All three documents are listed and readable here, collapsed by default so the
 * dialog opens at a readable size. Only the Terms are agreed to — see the checkbox
 * wording below, which is the whole point of there being one checkbox rather than
 * three.
 *
 * There is deliberately no scroll-to-enable gate. Reading is offered, not enforced;
 * the tick against a named version is the evidence, and holding someone hostage to
 * a scrollbar buys nothing it does not already have.
 */
interface LegalAcceptanceDialogProps {
  /**
   * True the first time a customer ever accepts, which is the only time the communications line is
   * offered. On a later Terms revision it is absent: re-showing it would ask somebody to re-answer
   * something they had settled, and a pre-ticked box would quietly re-opt-in anyone who turned it off.
   */
  firstAcceptance: boolean;
}

export function LegalAcceptanceDialog({ firstAcceptance }: LegalAcceptanceDialogProps) {
  const { t } = useTranslation();
  const { instance, accounts } = useMsal();
  const [accepted, setAccepted] = useState(false);
  const [emailOptIn, setEmailOptIn] = useState(true);
  const [expanded, setExpanded] = useState<string[]>([]);
  const panelRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const accept = useAcceptLegalDocuments();
  const preferences = useCommunicationPreferences();
  const savePreferences = useUpdateCommunicationPreferences();

  // Read from the API rather than listed here, so adding a category does not need this dialog
  // changing. Signals are the one the tick does not cover: they are the service the customer pays
  // for rather than marketing, so they start on either way.
  const optionalPreferences = (preferences.data ?? []).filter((p) => p.optional);
  const tickCovers = optionalPreferences.filter((p) => p.category !== CommunicationCategory.Signals);

  const toggle = (slug: string) =>
    setExpanded((current) =>
      current.includes(slug) ? current.filter((s) => s !== slug) : [...current, slug]
    );

  // Following a cross-reference from inside one document opens the one it points
  // at and brings it into view. Without the scroll the panel expands below the
  // fold and the link looks like it did nothing.
  const openDocument = useCallback((slug: string) => {
    setExpanded((current) => (current.includes(slug) ? current : [...current, slug]));
    requestAnimationFrame(() => {
      panelRefs.current[slug]?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, []);

  /**
   * Acceptance first, then preferences. Two messages, as the Back Office consumes them separately —
   * and the acceptance is what must not be lost: if the preferences call fails the customer is
   * through the gate with nothing optional stored, which is the safe way round. Failing the whole
   * thing would leave them unable to use the portal over a marketing tick.
   */
  const handleAccept = async () => {
    await accept.mutateAsync();

    if (!firstAcceptance) {
      return;
    }

    // An empty list would mean "nothing optional", Signals included, so a failed load must not be
    // mistaken for a customer who wants none — say nothing rather than the opposite of what they chose.
    if (optionalPreferences.length === 0) {
      return;
    }

    try {
      await savePreferences.mutateAsync(
        optionalPreferences
          .filter((p) => emailOptIn || p.category === CommunicationCategory.Signals)
          .map((p) => ({ category: p.category, channel: p.channel })),
      );
    } catch {
      // Swallowed on purpose. They are in, and nothing optional is stored, which is fail-closed —
      // they can set it from their profile whenever they like.
    }
  };

  // Given the account, MSAL sends its login_hint claim as the logout hint, so Entra signs it out
  // without first asking which account is leaving.
  const handleSignOut = () => {
    const logoutRequest = { account: accounts[0], postLogoutRedirectUri: window.location.origin };
    return instance.logoutRedirect(logoutRequest);
  };

  return (
    <CModal
      visible
      alignment="center"
      size="lg"
      // Full screen below md: at that width a dialog with nineteen sections in it
      // should behave like a page, not a card floating on a backdrop.
      fullscreen="md"
      // The one scroll container. Documents expand into this body and it grows;
      // the checkbox and buttons stay pinned below, so accepting never means
      // scrolling past a whole legal agreement to reach the button. Nothing inside
      // gets its own scrollbar — nested scrollers are what fight a thumb on a phone.
      scrollable
      backdrop="static"
      keyboard={false}
      className="legal-acceptance-dialog"
      aria-labelledby="legal-acceptance-title"
    >
      <CModalHeader closeButton={false}>
        <CModalTitle id="legal-acceptance-title" className="d-flex align-items-center gap-2">
          <ScryerSpark size={24} />
          {t("legal.acceptance.title")}
        </CModalTitle>
      </CModalHeader>

      <CModalBody>
        <p className="text-body-secondary">{t("legal.acceptance.intro")}</p>

        <div className="legal-acceptance-documents">
          {legalAcceptanceDocuments.map((doc) => {
            const isOpen = expanded.includes(doc.slug);
            const panelId = `legal-acceptance-panel-${doc.slug}`;

            return (
              <div
                key={doc.slug}
                className="legal-acceptance-document"
                ref={(el) => {
                  panelRefs.current[doc.slug] = el;
                }}
              >
                <button
                  type="button"
                  className="legal-acceptance-document-header"
                  onClick={() => toggle(doc.slug)}
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                >
                  <span className="legal-acceptance-document-heading">
                    <span className="fw-semibold">{doc.title}</span>
                    <span className="text-body-secondary small font-monospace">
                      {t("legal.lastUpdated", { date: doc.version })}
                    </span>
                  </span>
                  <span className="legal-acceptance-document-toggle" aria-hidden="true">
                    {isOpen ? "▾" : "▸"}
                  </span>
                  <span className="visually-hidden">
                    {isOpen
                      ? t("legal.acceptance.collapse", { document: doc.title })
                      : t("legal.acceptance.expand", { document: doc.title })}
                  </span>
                </button>

                {isOpen && (
                  <div id={panelId} className="legal-acceptance-document-body help-article legal-article">
                    <LegalDocumentBody
                      body={doc.body}
                      headingAnchors={false}
                      onNavigateToDocument={openDocument}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {accept.isError && (
          <CAlert color="danger" className="mt-3 mb-0">
            {isRetryable(accept.error)
              ? t("legal.acceptance.failed")
              : t("legal.acceptance.refused")}
          </CAlert>
        )}
      </CModalBody>

      <CModalFooter className="legal-acceptance-footer">
        {/*
          One checkbox, not three. You accept a contract; you are given a privacy
          notice. Asking someone to "accept" the Privacy Policy would imply consent
          is our legal basis for processing, when the policy itself says the bases
          are performance of a contract and legitimate interests — the document
          would be arguing with itself. Disclosures are acknowledged, not accepted.
        */}
        {/* Above the acceptance and in the same container, so the two ticks share the footer's
            gutter — the body's is 4px wider, which put them visibly out of line. */}
        {firstAcceptance && tickCovers.length > 0 && (
          <CFormCheck
            id="legal-acceptance-comms-opt-in"
            className="legal-acceptance-check"
            checked={emailOptIn}
            disabled={accept.isPending}
            onChange={(e) => setEmailOptIn(e.target.checked)}
            label={t("legal.acceptance.comms.optIn")}
          />
        )}

        <CFormCheck
          id="legal-acceptance-confirm"
          className="legal-acceptance-check"
          checked={accepted}
          disabled={accept.isPending}
          onChange={(e) => setAccepted(e.target.checked)}
          label={
            <Trans
              i18nKey="legal.acceptance.confirm"
              components={{ strong: <strong /> }}
            />
          }
        />

        <div className="legal-acceptance-actions">
          <CButton
            color="secondary"
            variant="outline"
            onClick={handleSignOut}
            disabled={accept.isPending}
          >
            {t("legal.acceptance.signOut")}
          </CButton>
          <CButton
            color="primary"
            disabled={!accepted || accept.isPending}
            onClick={handleAccept}
          >
            {accept.isPending && <CSpinner size="sm" className="me-2" />}
            {t("legal.acceptance.accept")}
          </CButton>
        </div>
      </CModalFooter>
    </CModal>
  );
}
